// İSG uzmanı portalı — İş İzni oluştur/düzenle formu (Modal + RHF + Zod). api-contract
// §14.1: `POST /permits` (create) / `PUT /permits/{uuid}` (update). Alanlar: taşeron
// (create'te seçilir; **update'te değiştirilemez** — §14.1), **sahalar (çoklu; opsiyonel —
// §14 Faz 2; `siteUuids` liste, boş olabilir)**, görev türü
// (enum), başlık (zorunlu), açıklama, tehlike sınıfı (**§14.1 KANON** — izin tipinden
// türer: tip somut [1/2/3] ise otomatik doldurulur + kilitlenir [backend override eder];
// tip "Diğer" [hazardClass=null] ise zorunlu selektör gösterilir [1-3]), geçerlilik başlangıç/bitiş
// (datetime-local → UTC ISO wire; §4), notlar, **gerekli eğitimler** (**yalnız create** —
// aktif eğitimlerden çoklu seçim → `trainingUuids`; §17.2).
//
// **Personel açılışta seçilmez (§14.1).** İSG uzmanı izni açarken kimin geleceğini
// bilmeyebilir; create personel kapsamı ALMAZ (istek `personnelUuids` göndermez). Kapsam
// sonradan detay ekranından (§14.3 `POST /permits/{uuid}/personnel`) eklenir. Gerekli eğitim
// ise baştan (create) seçilebilir ve detayda da ekle/kaldır ile yönetilir (§17.2).
//
// **Düzenlenebilirlik (§14).** Yalnız Draft/Submitted düzenlenebilir; bu form sadece
// düzenlenebilir izinler için açılır (detay düzenle butonu aksi hâlde gizli). Böylece BE
// `409 PERMIT_NOT_EDITABLE` UI'da önlenir.

import { zodResolver } from '@hookform/resolvers/zod';
import {
  Alert,
  Button,
  EmptyState,
  Field,
  FormSection,
  type HazardClass,
  Input,
  Modal,
  Select,
  type SelectOption,
  Spinner,
  type WorkPermitDto,
  type WorkPermitSaveMode,
  contractorsApi,
  isApiError,
  permitTypesApi,
  permitsApi,
  resolveApiErrorMessage,
  settingsApi,
  trainingApi,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Save, Send } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { permitTypesKeys } from '../../lib/permitTypesKeys';
import { permitsKeys } from '../../lib/permitsKeys';
import { HAZARD_CLASSES } from './permitEnums';

/** Taşeron/saha/eğitim seçicilerini tek sayfada besleyecek makul üst sınır (tenant ölçeği). */
const OPTIONS_PAGE_SIZE = 200;

interface PermitFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Verilirse düzenleme; yoksa oluşturma. */
  permit?: WorkPermitDto | null;
  onSuccess?: (permit: WorkPermitDto) => void;
}

/** Boş/whitespace dizeyi `null`'a indirger (opsiyonel alanlar backend'e `null` gider). */
function nullable(value: string | undefined): string | null {
  return value && value.trim().length > 0 ? value.trim() : null;
}

/** UTC ISO → `yyyy-MM-ddTHH:mm` (datetime-local input; kullanıcı yerel saati; §4 sunum). */
function toLocalInput(iso: string | null | undefined): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** `yyyy-MM-ddTHH:mm` (yerel) → UTC ISO wire (§4). */
function fromLocalInput(local: string): string {
  return new Date(local).toISOString();
}

export function PermitFormModal({ open, onOpenChange, permit, onSuccess }: PermitFormModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isEdit = Boolean(permit);

  // Gerekli eğitim seçimi (yalnız create) — form dışı yerel durum. Detay ekranı (§17.2)
  // ekle/kaldır ile yönetmeyi sürdürür; bu seçim yalnız açılış (create) setini kurar.
  const [selectedTrainings, setSelectedTrainings] = useState<Set<string>>(new Set());

  // Kaydetme kipi (SPEC §2): hangi aksiyon butonu basıldıysa (Taslak Kaydet → "draft",
  // Onaya Gönder → "submit") onClick'te ref'e yazılır; form submit handler bu ref'i okur.
  // onClick submit event'inden ÖNCE çalıştığından değer mutasyon anında doğrudur.
  const saveModeRef = useRef<WorkPermitSaveMode>('draft');

  const contractorsQuery = useQuery({
    queryKey: ['permits', 'options', 'contractors'],
    queryFn: () => contractorsApi.listContractors({ page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    enabled: open && !isEdit,
    staleTime: 60_000,
  });
  const sitesQuery = useQuery({
    queryKey: ['permits', 'options', 'sites'],
    queryFn: () =>
      settingsApi.listSites({
        page: 1,
        pageSize: OPTIONS_PAGE_SIZE,
        sortBy: 'name',
        sortDir: 'asc',
      }),
    enabled: open,
    staleTime: 60_000,
  });
  // Aktif eğitimler (create gerekli-eğitim seçicisi; §16 `GET /trainings?isActive=true`).
  const trainingsQuery = useQuery({
    queryKey: ['permits', 'options', 'trainings'],
    queryFn: () =>
      trainingApi.listTrainings({ isActive: true, page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    enabled: open && !isEdit,
    staleTime: 60_000,
  });
  // Aktif iş izni tipleri (tip seçici; §22 `GET /permit-types?isActive=true`). Create ve edit'te
  // gerekir (pasif tip seçilemez — §14). Ayarlar ekranıyla anahtar PAYLAŞILIR (permitTypesKeys.options).
  const permitTypesQuery = useQuery({
    queryKey: permitTypesKeys.options(),
    queryFn: () =>
      permitTypesApi.listPermitTypes({ isActive: true, page: 1, pageSize: OPTIONS_PAGE_SIZE }),
    enabled: open,
    staleTime: 60_000,
  });

  const permitTypeOptions: SelectOption[] = useMemo(() => {
    const active = (permitTypesQuery.data?.items ?? []).map((pt) => ({
      value: pt.uuid,
      label: pt.name,
    }));
    // Düzenlenen iznin tipi pasif/silinmişse aktif listede olmayabilir — mevcut değer boş
    // görünmesin diye başa ekle (kullanıcı aktif bir tiple değiştirebilir; §14 kaydetmede doğrular).
    if (permit?.permitTypeUuid && !active.some((o) => o.value === permit.permitTypeUuid)) {
      return [
        { value: permit.permitTypeUuid, label: permit.permitTypeName ?? permit.permitTypeUuid },
        ...active,
      ];
    }
    return active;
  }, [permitTypesQuery.data, permit]);

  const noPermitTypes = permitTypesQuery.isSuccess && permitTypeOptions.length === 0;
  const hazardOptions: SelectOption[] = useMemo(
    () => HAZARD_CLASSES.map((h) => ({ value: String(h), label: t(`hazard.${h}`) })),
    [t],
  );

  // Tip → tehlike sınıfı eşlemesi (§14.1 KANON): her aktif izin tipinin `hazardClass`'ı
  // (`int?`; somut 1/2/3 veya `null`="Diğer"). Seçilen tip somutsa sınıf tipten gelir + kilitlenir;
  // "Diğer" (null) ise kullanıcı 1-3 elle seçer (zorunlu). (Selectlist = `GET /permit-types`; §22.)
  const hazardByPermitType = useMemo(() => {
    const map = new Map<string, HazardClass | null>();
    for (const pt of permitTypesQuery.data?.items ?? []) {
      map.set(pt.uuid, pt.hazardClass);
    }
    return map;
  }, [permitTypesQuery.data]);
  const contractorOptions: SelectOption[] = useMemo(
    () => (contractorsQuery.data?.items ?? []).map((c) => ({ value: c.uuid, label: c.name })),
    [contractorsQuery.data],
  );
  // Çoklu-saha seçici (§14 Faz 2) — aktif sahalar; seçim `siteUuids` listesine yazılır (boş olabilir).
  const siteItems = useMemo(() => sitesQuery.data?.items ?? [], [sitesQuery.data]);
  const trainingOptions = useMemo(() => trainingsQuery.data?.items ?? [], [trainingsQuery.data]);

  const schema = useMemo(
    () =>
      z
        .object({
          contractorUuid: z.string().min(1, t('permits.validation.contractorRequired')),
          // Çoklu-saha (§14 Faz 2) — opsiyonel; boş liste = saha seçilmedi (izin sahasız açılabilir).
          siteUuids: z.array(z.string()),
          // İzin tipi (§22 firma-tanımlı) — Select aktif tiplerden uuid sunar; boş →
          // permitTypeRequired. Aktiflik/firma doğrulaması backend'de (§14 create/update).
          permitTypeUuid: z.string().min(1, t('permits.validation.permitTypeRequired')),
          title: z.string().trim().min(1, t('permits.validation.titleRequired')),
          description: z.string().trim().optional(),
          // §14.1: izin bir izin kaydı her zaman somut (1/2/3) taşır. Boş ('') yalnız tip
          // "Diğer" iken (henüz seçilmedi) mümkün → zorunlu uyarı (aşağıdaki refine). Tip somutsa
          // efekt otomatik doldurur → boş kalmaz.
          hazardClass: z.enum(['', '1', '2', '3']),
          // "Araç olacak mı?" bayrağı (§23) — araçların kendisi detayda eklenir; form yalnız bayrak.
          hasVehicles: z.boolean(),
          validityStart: z.string().min(1, t('permits.validation.validityStartRequired')),
          validityEnd: z.string().min(1, t('permits.validation.validityEndRequired')),
        })
        .refine((v) => !v.validityStart || !v.validityEnd || v.validityEnd >= v.validityStart, {
          path: ['validityEnd'],
          message: t('permits.validation.validityRange'),
        })
        // Tehlike sınıfı zorunlu: tip "Diğer" ise kullanıcı somut sınıf seçmeli (boş → submit
        // engelli). Tip somutsa efekt zaten doldurduğundan bu hata çıkmaz (§14.1).
        .refine((v) => v.hazardClass !== '', {
          path: ['hazardClass'],
          message: t('permits.validation.hazardClassRequired'),
        }),
    [t],
  );
  type FormValues = z.infer<typeof schema>;

  const { register, handleSubmit, reset, watch, setValue, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
    values: {
      contractorUuid: permit?.contractorUuid ?? '',
      siteUuids: permit?.sites.map((s) => s.uuid) ?? [],
      permitTypeUuid: permit?.permitTypeUuid ?? '',
      title: permit?.title ?? '',
      description: permit?.description ?? '',
      // Create'te boş başlar (tip seçilince efekt doldurur/selektör açar; §14.1). Edit'te
      // mevcut somut sınıf (1/2/3) ile başlar; tip somutsa efekt tipten yeniden doldurur.
      hazardClass: (permit ? String(permit.hazardClass) : '') as '' | '1' | '2' | '3',
      hasVehicles: permit?.hasVehicles ?? false,
      validityStart: toLocalInput(permit?.validityStart) || '',
      validityEnd: toLocalInput(permit?.validityEnd) || '',
    },
  });

  // Seçili izin tipinin tehlike sınıfı (§14.1). `undefined` = tip henüz seçilmedi / aktif
  // listede değil (edit'te pasif/silinmiş tip prepend'i) → manuel selektör. `null` = "Diğer"
  // → manuel selektör (zorunlu). `1/2/3` = somut → otomatik doldur + kilitle.
  const watchedPermitType = watch('permitTypeUuid');
  const selectedTypeHazard = hazardByPermitType.get(watchedPermitType);
  const hazardLocked =
    selectedTypeHazard === 1 || selectedTypeHazard === 2 || selectedTypeHazard === 3;

  // Tip seçimine göre tehlike sınıfını yeniden değerlendir (§14.1): somut tip → sınıfı tipten
  // otomatik doldur (backend zaten override eder); "Diğer"/null'a geçiş → alanı temizle ki
  // kullanıcı bilinçli seçsin (zorunlu). İlk render'da (prev == mevcut) tip değişmediği için
  // edit'teki mevcut "Diğer" değeri korunur; tip verisi sonradan yüklenince somut tip doldurulur.
  const prevPermitTypeRef = useRef(permit?.permitTypeUuid ?? '');
  useEffect(() => {
    const typeChanged = watchedPermitType !== prevPermitTypeRef.current;
    prevPermitTypeRef.current = watchedPermitType;
    if (selectedTypeHazard === 1 || selectedTypeHazard === 2 || selectedTypeHazard === 3) {
      setValue('hazardClass', String(selectedTypeHazard) as '1' | '2' | '3', {
        shouldValidate: true,
      });
    } else if (typeChanged) {
      setValue('hazardClass', '', { shouldValidate: true });
    }
  }, [watchedPermitType, selectedTypeHazard, setValue]);

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      const base = {
        // Çoklu-saha (§14 Faz 2): create'te iznin kapsayacağı sahalar, update'te saha kümesini
        // **replace** eder (boş liste tüm sahaları temizler). Her saha aynı firmada doğrulanır (BE).
        siteUuids: values.siteUuids,
        // İzin tipi (§22) — Select aktif tip uuid'i verir; aktiflik/firma backend'de doğrulanır.
        permitTypeUuid: values.permitTypeUuid,
        title: values.title.trim(),
        description: nullable(values.description),
        hazardClass: Number(values.hazardClass) as HazardClass,
        // "Araç olacak mı?" bayrağı (§23) — create/update gövdesinde taşınır; araçlar detayda eklenir.
        hasVehicles: values.hasVehicles,
        validityStart: fromLocalInput(values.validityStart),
        validityEnd: fromLocalInput(values.validityEnd),
        // `notes` UI'da yönetilmiyor: okuma modeli (WorkPermitDto) notes döndürmediğinden
        // düzenlemede mevcut değer yüklenemez; PUT unconditional overwrite yaptığı için forma
        // koymak sessiz veri kaybı olurdu. Notes create/update gövdesine HİÇ eklenmez (mevcut
        // sunucu değeri create'te null kalır; düzenlemede round-trip edilemeyeceğinden alan
        // tamamen kaldırıldı — brief'in form alanı listesinde de yer almıyordu). Not düzenleme
        // istenirse backend WorkPermitDto'ya notes eklemeli (ayrı brief).
      };
      if (permit) {
        return permitsApi.updatePermit(permit.uuid, base);
      }
      // Create personel kapsamı ALMAZ (§14.1) — personel sonradan §14.3 ile eklenir. Yalnız
      // (opsiyonel) gerekli eğitim listesi gönderilir; boşsa alan atlanır (undefined).
      // saveMode (SPEC §2): "draft" → Draft; "submit" → onaya gönder (backend açanın reviewer'lığına
      // göre oto-onay/Submitted karar verir). Kipi basılan butonun ref'inden okuruz.
      const trainingUuids = [...selectedTrainings];
      return permitsApi.createPermit({
        ...base,
        contractorUuid: values.contractorUuid,
        trainingUuids: trainingUuids.length > 0 ? trainingUuids : undefined,
        saveMode: saveModeRef.current,
      });
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: permitsKeys.list() });
      queryClient.invalidateQueries({ queryKey: permitsKeys.access() });
      if (permit) {
        queryClient.invalidateQueries({ queryKey: permitsKeys.detail(permit.uuid) });
      }
      reset();
      setSelectedTrainings(new Set());
      onSuccess?.(saved);
      onOpenChange(false);
    },
  });

  // Kaydetme hatası kullanıcı-dostu metne çözülür. Tip "Diğer" iken tehlike sınıfı boş
  // kalırsa client-side refine zaten engeller; yine de backend 400 VALIDATION_FAILED +
  // alan kodu `HAZARD_CLASS_REQUIRED` dönerse (edge) ham kod yerine özel uyarı gösterilir (§14.1).
  const submitErrorMessage = useMemo(() => {
    const err = mutation.error;
    if (
      isApiError(err) &&
      err.errorCode === 'VALIDATION_FAILED' &&
      err.errors.some((e) => e.code === 'HAZARD_CLASS_REQUIRED')
    ) {
      return t('permits.validation.hazardClassRequired');
    }
    return resolveApiErrorMessage(err, t);
  }, [mutation.error, t]);

  function toggleTraining(uuid: string) {
    setSelectedTrainings((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }

  // Çoklu-saha seçimi (§14 Faz 2) — RHF `siteUuids` dizisini yönetir (create + edit). Edit'te
  // başlangıç seti `permit.sites`'ten `values` ile senkronlanır; toggle diziyi yeniden yazar.
  const selectedSites = watch('siteUuids');
  function toggleSite(uuid: string) {
    const next = selectedSites.includes(uuid)
      ? selectedSites.filter((u) => u !== uuid)
      : [...selectedSites, uuid];
    setValue('siteUuids', next, { shouldDirty: true });
  }

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          mutation.reset();
          reset();
          setSelectedTrainings(new Set());
        }
        onOpenChange(next);
      }}
      title={isEdit ? t('permits.form.edit.title') : t('permits.form.create.title')}
      description={t('permits.form.description')}
      size="lg"
      closeLabel={t('permits.common.close')}
      // Create modu 3 eşit-genişlikli buton taşır (Vazgeç / Taslak Kaydet / Onaya Gönder) —
      // Modal'ın varsayılan 2 sütunlu Carbon footer'ı bunları alt satıra taşırdı (S33-8 review
      // fix #1). Edit modu 2 butonla varsayılanı korur.
      footerColumns={isEdit ? 2 : 3}
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('permits.common.cancel')}
          </Button>
          {isEdit ? (
            <Button
              type="submit"
              form="permit-form"
              loading={mutation.isPending}
              disabled={noPermitTypes}
            >
              {mutation.isPending ? (
                <Spinner label={t('permits.common.save')} />
              ) : (
                t('permits.common.save')
              )}
            </Button>
          ) : (
            // Açılışta iki aksiyon (SPEC §2): Taslak Kaydet (saveMode "draft") + Onaya Gönder
            // (saveMode "submit"). İkisi de aynı formu gönderir; basılan buton ref'e kipini yazar.
            <>
              <Button
                type="submit"
                form="permit-form"
                variant="secondary"
                disabled={mutation.isPending || noPermitTypes}
                onClick={() => {
                  saveModeRef.current = 'draft';
                }}
              >
                <Save className="h-4 w-4" aria-hidden="true" />
                {t('permits.form.saveDraft')}
              </Button>
              <Button
                type="submit"
                form="permit-form"
                loading={mutation.isPending}
                disabled={noPermitTypes}
                onClick={() => {
                  saveModeRef.current = 'submit';
                }}
              >
                {mutation.isPending ? (
                  <Spinner label={t('permits.form.submitForApproval')} />
                ) : (
                  <>
                    <Send className="h-4 w-4" aria-hidden="true" />
                    {t('permits.form.submitForApproval')}
                  </>
                )}
              </Button>
            </>
          )}
        </>
      }
    >
      <form
        id="permit-form"
        className="flex flex-col gap-8"
        onSubmit={handleSubmit((values) => mutation.mutate(values))}
        noValidate
      >
        {mutation.isError ? <Alert tone="danger">{submitErrorMessage}</Alert> : null}

        {/* Görev bilgileri */}
        <FormSection legend={t('permits.form.section.task')}>
          {isEdit ? (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-muted">
                {t('permits.form.field.contractor')}
              </span>
              <span className="text-sm text-fg">{permit?.contractorName ?? '—'}</span>
            </div>
          ) : (
            <Field
              htmlFor="permit-contractor"
              label={t('permits.form.field.contractor')}
              error={formState.errors.contractorUuid?.message}
              required
            >
              <Select
                id="permit-contractor"
                invalid={Boolean(formState.errors.contractorUuid)}
                disabled={contractorsQuery.isPending}
                placeholder={t('permits.form.contractorPlaceholder')}
                options={contractorOptions}
                {...register('contractorUuid')}
              />
            </Field>
          )}

          {noPermitTypes ? (
            <EmptyState variant="panel" title={t('permits.form.noPermitTypes')} />
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              htmlFor="permit-type"
              label={t('permits.form.field.permitType')}
              error={formState.errors.permitTypeUuid?.message}
              required
            >
              <Select
                id="permit-type"
                invalid={Boolean(formState.errors.permitTypeUuid)}
                disabled={permitTypesQuery.isPending || noPermitTypes}
                placeholder={t('permits.form.permitTypePlaceholder')}
                options={permitTypeOptions}
                {...register('permitTypeUuid')}
              />
            </Field>
            {/* Tehlike sınıfı (§14.1 KANON): tip somut → tipten otomatik doldur + kilitle
                (read-only gösterim; backend override eder); tip "Diğer"/null → zorunlu selektör.
                Tip değişince efekt yeniden değerlendirir. */}
            {hazardLocked ? (
              <Field
                htmlFor="permit-hazard-locked"
                label={t('permits.form.field.hazardClass')}
                required
              >
                <div
                  id="permit-hazard-locked"
                  className="flex h-10 items-center rounded-md border border-border-strong bg-surface-muted px-3 text-sm text-fg"
                >
                  {selectedTypeHazard ? t(`hazard.${selectedTypeHazard}`) : ''}
                </div>
                <p className="mt-1 text-xs text-muted">{t('permits.form.hazardFromTypeHint')}</p>
              </Field>
            ) : (
              <Field
                htmlFor="permit-hazard"
                label={t('permits.form.field.hazardClass')}
                error={formState.errors.hazardClass?.message}
                required
              >
                <Select
                  id="permit-hazard"
                  invalid={Boolean(formState.errors.hazardClass)}
                  placeholder={t('permits.form.hazardPlaceholder')}
                  options={hazardOptions}
                  {...register('hazardClass')}
                />
                <p className="mt-1 text-xs text-muted">{t('permits.form.hazardManualHint')}</p>
              </Field>
            )}
          </div>

          <Field
            htmlFor="permit-title"
            label={t('permits.form.field.title')}
            error={formState.errors.title?.message}
            required
          >
            <Input
              id="permit-title"
              invalid={Boolean(formState.errors.title)}
              {...register('title')}
            />
          </Field>

          <Field htmlFor="permit-description" label={t('permits.form.field.description')}>
            <Input id="permit-description" {...register('description')} />
          </Field>

          {/* "Araç olacak mı?" bayrağı (§23) — işaretlenirse detayda araç bölümü açılır (plaka +
              evrak). Araçların kendisi burada eklenmez; form yalnız bayrağı taşır. */}
          <label className="flex items-center gap-2 text-sm text-fg">
            <input
              type="checkbox"
              className="h-4 w-4 accent-primary"
              {...register('hasVehicles')}
            />
            <span className="flex flex-col">
              <span className="font-medium">{t('permits.form.field.hasVehicles')}</span>
              <span className="text-xs text-muted">{t('permits.form.hasVehiclesHint')}</span>
            </span>
          </label>
        </FormSection>

        {/* Sahalar (§14 Faz 2 çoklu-lokasyon) — opsiyonel çoklu seçim; boş bırakılabilir. */}
        <FormSection legend={t('permits.form.field.sites')} className="gap-2">
          <p className="text-xs text-muted">{t('permits.form.sitesNote')}</p>
          {sitesQuery.isPending ? (
            <Spinner label={t('permits.form.sitesLoading')} />
          ) : siteItems.length === 0 ? (
            <EmptyState variant="panel" title={t('permits.form.sitesEmpty')} />
          ) : (
            // `max-h`/`overflow-y-auto` kaldırıldı (S33-8 review fix #1) — Modal gövdesi artık
            // KENDİSİ kayıyor (bkz. `Modal.tsx`), iç içe ikinci bir kaydırma kutusu gereksizdi.
            <div className="flex flex-col gap-1 rounded-md border border-border p-2">
              {siteItems.map((site) => (
                <label
                  key={site.uuid}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={selectedSites.includes(site.uuid)}
                    onChange={() => toggleSite(site.uuid)}
                  />
                  <span className="font-medium">
                    {site.branchName ? `${site.name} · ${site.branchName}` : site.name}
                  </span>
                </label>
              ))}
            </div>
          )}
        </FormSection>

        {/* Geçerlilik */}
        <FormSection legend={t('permits.form.section.validity')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              htmlFor="permit-start"
              label={t('permits.form.field.validityStart')}
              error={formState.errors.validityStart?.message}
              required
            >
              <Input
                id="permit-start"
                type="datetime-local"
                invalid={Boolean(formState.errors.validityStart)}
                {...register('validityStart')}
              />
            </Field>
            <Field
              htmlFor="permit-end"
              label={t('permits.form.field.validityEnd')}
              error={formState.errors.validityEnd?.message}
              required
            >
              <Input
                id="permit-end"
                type="datetime-local"
                invalid={Boolean(formState.errors.validityEnd)}
                {...register('validityEnd')}
              />
            </Field>
          </div>
        </FormSection>

        {/* Gerekli eğitimler — yalnız create (aktif eğitimlerden çoklu seçim → trainingUuids;
            §17.2). Personel açılışta seçilmez (§14.1); detay ekranından eklenir. */}
        {!isEdit ? (
          <FormSection legend={t('permits.form.section.trainings')} className="gap-3">
            <p className="text-xs text-muted">{t('permits.form.trainingsNote')}</p>
            {trainingsQuery.isPending ? (
              <Spinner label={t('permits.form.trainingsLoading')} />
            ) : trainingOptions.length === 0 ? (
              <EmptyState variant="panel" title={t('permits.form.trainingsEmpty')} />
            ) : (
              // `max-h`/`overflow-y-auto` kaldırıldı (S33-8 review fix #1) — bkz. sahalar
              // listesindeki aynı not.
              <div className="flex flex-col gap-1 rounded-md border border-border p-2">
                {trainingOptions.map((training) => (
                  <label
                    key={training.uuid}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm text-fg hover:bg-surface-muted"
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-primary"
                      checked={selectedTrainings.has(training.uuid)}
                      onChange={() => toggleTraining(training.uuid)}
                    />
                    <span className="font-medium">{training.title}</span>
                  </label>
                ))}
              </div>
            )}
          </FormSection>
        ) : null}
      </form>
    </Modal>
  );
}
